import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Table, UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  updateETRackerMilestone,
  updateBenchmarkingMilestone,
  updateLandscapingMilestone
} from "../../services/milestoneServices";
import { fetchActiveUsers, fetchSupervisors } from "services/userServices";
import Select from 'react-select';
import "./FormsTable.css";
import { theme } from "common/selectCustomStyles";

const StyledTableContainer = styled.div`
  width: 100%;
  padding: 1em;
  overflow-x: auto; /* Allows horizontal scroll if necessary */
`;

const StyledTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
  background-color: transparent;
  border-radius: 16px 16px 0px 0px;
  overflow: hidden;

  & thead {
    background-color: #f8f9fa;
  }

  & td {
    padding: 1em;
    text-align: left;
    font-size: 14px;
    font-weight: 500;
    border: none; /* Remove all borders */
    border-bottom: 1px solid #dee2e6; /* Only keep the bottom border */
    vertical-align: middle;
    background-color: white; /* Ensure the background is white */
  }

  & tbody {
    & tr {
      background-color: white !important; /* Ensure all rows have a white background */
      cursor: default;
      &:hover {
        background-color: #f8f9fa; /* Add a hover effect for rows */
      }
      & .description_cell {
        white-space: pre-wrap;
        word-wrap: break-word;
        text-overflow: ellipsis;
        overflow-wrap: break-word;
        max-width: 200px;
      }
    }
  }

  & tbody tr td:first-of-type {
    .name-cell {
      padding: 0.2em 0.4em;
      border-radius: 0.8em;
      background-color: rgba(80, 165, 241, 0.1);
      color: rgba(80, 165, 241);
    }
  }
`;

const EditOwnerSupervisorModal = ({ isOpen, toggle, field, row, formType, onUpdate }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState(null);

  // Fetch dynamic options for owners or supervisors based on the field.
  useEffect(() => {
    const getOptions = async () => {
      try {
        if (field === "owner") {
          const activeUsers = await fetchActiveUsers();
          const mapped = activeUsers
            .map(user => ({
              value: user.id,
              label: `${user.first_name} ${user.last_name} (${user.user_country?.[0]?.country?.short_name || "N/A"})`
            }));
          setOptions(mapped);
          
          // Set current owner as the default selected option
          if (row.owner_and_supervisor?.owner_id) {
            const currentOwner = mapped.find(option => 
              option.value === row.owner_and_supervisor.owner_id
            );
            if (currentOwner) {
              setSelectedOption(currentOwner);
              setCurrentValue(currentOwner.value);
            }
          }
        } else if (field === "supervisor") {
          const supervisors = await fetchSupervisors();
          const mapped = supervisors
            .map(user => ({
              value: user.id,
              label: `${user.first_name} ${user.last_name} (${user.user_country?.[0]?.country?.short_name || "N/A"})`
            }));
          setOptions(mapped);
          
          // Set current supervisor as the default selected option
          if (row.owner_and_supervisor?.supervisor_id) {
            const currentSupervisor = mapped.find(option => 
              option.value === row.owner_and_supervisor.supervisor_id
            );
            if (currentSupervisor) {
              setSelectedOption(currentSupervisor);
              setCurrentValue(currentSupervisor.value);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching dynamic options:", error);
      }
    };
    getOptions();
  }, [field, row]);

  const handleSelectChange = (option) => {
    setSelectedOption(option);
  };

  const handleUpdate = async () => {
    if (!selectedOption) return; // No selection made
    
    const milestoneId = row.id;
    let body = {};
    if (field === "owner") {
      body = {
        owner_id: selectedOption.value,
        supervisor_id: row.owner_and_supervisor?.supervisor_id || 0,
      };
    } else if (field === "supervisor") {
      body = {
        owner_id: row.owner_and_supervisor?.owner_id || 0,
        supervisor_id: selectedOption.value,
      };
    }

    let updateFunction;
    switch (formType) {
      case "tracker":
        updateFunction = updateETRackerMilestone;
        break;
      case "benchmarking":
        updateFunction = updateBenchmarkingMilestone;
        break;
      case "landscaping":
        updateFunction = updateLandscapingMilestone;
        break;
      default:
        console.error("Unsupported form type");
        return;
    }

    try {
      setLoading(true);
      const response = await updateFunction(milestoneId, body);

      if(response){
        // Create an updated row with the new owner/supervisor information
        const updatedRow = {
          ...row,
          owner_and_supervisor: {
            ...row.owner_and_supervisor,
            ...(field == 'owner' || field == 'supervisor') && {[field]: {
              first_name: response[field].first_name,
              last_name: response[field].last_name,
            }},
            owner_id: response.owner_id,
            supervisor_id: response.supervisor_id
          }
        };
        
        // Pass the updated row back to parent component
        onUpdate(updatedRow);
        //add alert
      }
      toggle(); // Close the modal
    } catch (error) {
      //add alert
      console.error("Error updating milestone:", error);
    } finally{
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <h4 className="text-slate-800 font-medium text-xl ml-2 flex items-center">
          <i className="bx bx-pencil hover-event me-2"></i>
          Edit {field === "owner" ? "Owner" : "Supervisor"}
        </h4>
      </ModalHeader>
      <ModalBody>
        <div className="flex items-center space-x-3">
          <h5 className="font-medium">
            Select {field === "owner" ? "Owner" : "Supervisor"}
          </h5>
          <Select
            onChange={handleSelectChange}
            options={options}
            value={selectedOption}
            theme = {theme}
            styles={{
              container: (provided) => ({
                  ...provided,
                  width: "auto",
                  minWidth: "160px"
              }),
            }}
          />
        </div>
        <div className="flex justify-center">
          <button
            className="common-button mb-0"
            onClick={handleUpdate}
            disabled={loading || (selectedOption?.value === currentValue)}>
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

const ETrackerBenchmarkingTable = ({
  forms,
  userRole,
  formType,
  initiateEdit,
  onDeleteClick,
  caption,
  adminCountryLength,
  openEditModal
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletionObjectId, setDeletionObjectId] = useState(null);
  const user = useSelector(state => state.Profile.user);

  return (
    <StyledTableContainer>
      <StyledTable className="custom-table">
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "90px" }} />
        </colgroup>
        <thead className="table-header">
          <tr>
            <th scope="col">Milestone Name</th>
            <th scope="col">{formType === "ETracker" ? "Stage" : "System"}</th>
            <th scope="col">Owner</th>
            <th scope="col">Supervisor</th>
            <th scope="col">Created By</th>
            <th scope="col">Updated By</th>
            <th className="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          {forms && forms.length > 0 ? (
            forms.map((row) => (
              <tr key={row.id}>
                <td style={{ color: "#495057" }}>{row?.name || ""}</td>
                <td>
                  {formType === "ETracker" ? row?.stage?.name || "" : row?.system?.name || ""}
                </td>
                {/* <td>
                    <i
                      className="mdi mdi-pencil font-size-16 text-primary"
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                      onClick={() => openEditModal(row, "owner")}
                      id={`editOwner-${row.id}`}
                    />
                    <UncontrolledTooltip placement="top" target={`editOwner-${row.id}`}>
                      Edit Owner
                    </UncontrolledTooltip>
                    <span>
                      {`${row.owner_and_supervisor?.owner?.first_name || ""} ${row.owner_and_supervisor?.owner?.last_name || ""}`.trim() || ""}
                    </span>
                  </td>
                  <td>
                    <i
                      className="mdi mdi-pencil font-size-16 text-primary"
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                      onClick={() => openEditModal(row, "supervisor")}
                      id={`editSupervisor-${row.id}`}
                    />
                    <UncontrolledTooltip placement="top" target={`editSupervisor-${row.id}`}>
                      Edit Supervisor
                    </UncontrolledTooltip>
                    <span>
                      {`${row.owner_and_supervisor?.supervisor?.first_name || ""} ${row.owner_and_supervisor?.supervisor?.last_name || ""}`.trim() || ""}
                    </span>
                  </td> */}
                <td>
                  {user?.user_country?.length === 1 && (
                    <>
                      <i
                        className="mdi mdi-pencil font-size-16 text-primary"
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                        onClick={() => openEditModal(row, "owner")}
                        id={`editOwner-${row.id}`}
                      />
                      <UncontrolledTooltip placement="top" target={`editOwner-${row.id}`}>
                        Edit Owner
                      </UncontrolledTooltip>
                    </>
                  )}
                  <span>
                    {`${row.owner_and_supervisor?.owner?.first_name || ""} ${row.owner_and_supervisor?.owner?.last_name || ""}`.trim() || ""}
                  </span>
                </td>

                <td>
                  {user?.user_country?.length === 1 && (
                    <>
                      <i
                        className="mdi mdi-pencil font-size-16 text-primary"
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                        onClick={() => openEditModal(row, "supervisor")}
                        id={`editSupervisor-${row.id}`}
                      />
                      <UncontrolledTooltip placement="top" target={`editSupervisor-${row.id}`} >
                        Edit Supervisor
                      </UncontrolledTooltip>
                    </>
                  )}
                  <span>
                    {`${row.owner_and_supervisor?.supervisor?.first_name || ""} ${row.owner_and_supervisor?.supervisor?.last_name || ""}`.trim() || ""}
                  </span>
                </td>
                <td>
                  {`${row.created_by?.first_name || ""} ${row.created_by?.last_name || ""}`.trim() || ""}
                  <br />
                  <small style={{ fontSize: "0.7rem" }} className="text-muted">
                    {row?.created_at || ""}
                  </small>
                </td>
                <td>
                  {`${row.updated_by?.first_name || ""} ${row.updated_by?.last_name || ""}`.trim() || ""}
                  <br />
                  <small style={{ fontSize: "0.7rem" }} className="text-muted">
                    {row?.updated_at || ""}
                  </small>
                </td>
                <td className="actions-column">
                  {((row?.is_locked && adminCountryLength === 1) || (!row?.is_locked && adminCountryLength > 1)) ? (
                    <div className="d-flex justify-center">
                      <i className="mdi mdi-lock font-size-18 text-muted" id={`locktooltip-${row.id}`} />
                      <UncontrolledTooltip placement="top" target={`locktooltip-${row.id}`}>
                        Locked
                      </UncontrolledTooltip>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between">
                      <Link
                        to={{
                          pathname: "/edit-form",
                          state: { userRole: userRole }
                        }}
                        className="text-success"
                        onClick={() => initiateEdit(row.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="mdi mdi-pencil font-size-18" id={`edittooltip-${row.id}`} />
                        <UncontrolledTooltip placement="top" target={`edittooltip-${row.id}`}>
                          Edit
                        </UncontrolledTooltip>
                      </Link>
                      <p
                        style={{ cursor: "pointer" }}
                        className="text-danger m-0"
                        onClick={() => {
                          setDeleteModal({ show: true });
                          setDeletionObjectId(row.id);
                        }}
                      >
                        <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${row.id}`} />
                        <UncontrolledTooltip placement="top" target={`deletetooltip-${row.id}`}>
                          Delete
                        </UncontrolledTooltip>
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">No data available</td>
            </tr>
          )}
        </tbody>
        <DeleteModal
          show={deleteModal.show}
          onCloseClick={() => setDeleteModal(false)}
          onDeleteClick={() => onDeleteClick(deletionObjectId)}
          item={caption}
        />
      </StyledTable>
    </StyledTableContainer>
  );
};

const LandscapingTable = ({
  forms,
  userRole,
  initiateEdit,
  onDeleteClick,
  caption,
  adminCountryLength,
  openEditModal,
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletionObjectId, setDeletionObjectId] = useState(null);
  const user = useSelector(state => state.Profile.user);

  return (
    <StyledTableContainer>
      <StyledTable className="custom-table">
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "35%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "90px" }} />
        </colgroup>
        <thead className="table-header">
          <tr>
            <th scope="col">Focal Area</th>
            <th scope="col"> Landscaping Category</th>
            <th scope="col"> Description</th>
            <th scope="col"> Owner</th>
            <th scope="col"> Supervisor</th>
            <th scope="col">Created By</th>
            <th scope="col">Updated By</th>
            <th className="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          {forms && forms.length > 0 ? (
            forms.map((row) => (
              <tr key={row.id}>
                <td style={{ color: "#495057" }}>{row?.name || ""}</td>
                <td>{row?.landscaping_category || ""}</td>
                <td>{row?.description || ""}</td>
                <td>
                  {user?.user_country?.length === 1 && ( // ✅ Only show if Country Admin
                    <>
                      <i
                        className="mdi mdi-pencil font-size-16 text-primary cursor-pointer"
                        onClick={() => openEditModal(row, "owner")}
                        id={`editOwner-${row.id}`}
                      />
                      <UncontrolledTooltip placement="top" target={`editOwner-${row.id}`}>
                        Edit Owner
                      </UncontrolledTooltip>
                    </>
                  )}
                  <span>
                    {`${row.owner_and_supervisor?.owner?.first_name || ""} ${row.owner_and_supervisor?.owner?.last_name || ""}`.trim() || ""}
                  </span>
                </td>

                <td>
                  {user?.user_country?.length === 1 && ( // ✅ Only show if Country Admin
                    <>
                      <i
                        className="mdi mdi-pencil font-size-16 text-primary"
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                        onClick={() => openEditModal(row, "supervisor")}
                        id={`editSupervisor-${row.id}`}
                      />
                      <UncontrolledTooltip placement="top" target={`editSupervisor-${row.id}`}>
                        Edit Supervisor
                      </UncontrolledTooltip>
                    </>
                  )}
                  <span>
                    {`${row.owner_and_supervisor?.supervisor?.first_name || ""} ${row.owner_and_supervisor?.supervisor?.last_name || ""}`.trim() || ""}
                  </span>
                </td>
                <td>
                  {`${row.created_by?.first_name || ""} ${row.created_by?.last_name || ""}`.trim() || ""}
                  <br />
                  <small style={{ fontSize: "0.7rem" }} className="text-muted">
                    {row?.created_at || ""}
                  </small>
                </td>
                <td>
                  {`${row.updated_by?.first_name || ""} ${row.updated_by?.last_name || ""}`.trim() || ""}
                  <br />
                  <small style={{ fontSize: "0.7rem" }} className="text-muted">
                    {row?.updated_at || ""}
                  </small>
                </td>
                <td className="actions-column">
                  {((row?.is_locked && adminCountryLength === 1) || (!row?.is_locked && adminCountryLength > 1)) ? (
                    <div className="d-flex justify-content-center">
                      <i className="mdi mdi-lock font-size-18 text-muted" id={`locktooltip-${row.id}`} />
                      <UncontrolledTooltip placement="top" target={`locktooltip-${row.id}`}>
                        Locked
                      </UncontrolledTooltip>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between">
                      <Link
                        to={{
                          pathname: "/edit-form",
                          state: { userRole: userRole }
                        }}
                        className="text-success"
                        onClick={() => initiateEdit(row.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="mdi mdi-pencil font-size-18" id={`edittooltip-${row.id}`} />
                        <UncontrolledTooltip placement="top" target={`edittooltip-${row.id}`}>
                          Edit
                        </UncontrolledTooltip>
                      </Link>
                      <p
                        style={{ cursor: "pointer" }}
                        className="text-danger m-0"
                        onClick={() => {
                          setDeleteModal({ show: true });
                          setDeletionObjectId(row.id);
                        }}
                      >
                        <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${row.id}`} />
                        <UncontrolledTooltip placement="top" target={`deletetooltip-${row.id}`}>
                          Delete
                        </UncontrolledTooltip>
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">No data available</td>
            </tr>
          )}
        </tbody>
        <DeleteModal
          show={deleteModal}
          onCloseClick={() => setDeleteModal(false)}
          onDeleteClick={() => onDeleteClick(deletionObjectId)}
          item={caption}
        />
      </StyledTable>
    </StyledTableContainer>
  );
};
const FormsTable = ({ formType, caption, forms, setForms, userRole, initiateEdit, onDeleteClick, adminCountryLength }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalField, setModalField] = useState(null);
  const [modalRow, setModalRow] = useState(null);

  const computedFormType = React.useMemo(() => {
    if (formType === "ETracker") return "tracker";
    if (formType === "Benchmarking") return "benchmarking";
    if (formType === "Landscaping") return "landscaping";
    return formType; // fallback if none match
  }, [formType]);

  const openEditModal = (row, field) => {
    setModalRow(row);
    setModalField(field);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setModalField(null);
    setModalRow(null);
  };

  const handleUpdate = (updatedRow) => {
    setForms(prevData => 
      prevData.map(row => 
        row.id === updatedRow.id ? updatedRow : row
      )
    );
  };

  const renderTable = () => {
    switch (formType) {
      case "ETracker":
      case "Benchmarking":
        return (
          <ETrackerBenchmarkingTable
            forms={forms}
            userRole={userRole}
            formType={formType}
            initiateEdit={initiateEdit}
            onDeleteClick={onDeleteClick}
            caption={caption}
            adminCountryLength={adminCountryLength}
            openEditModal={openEditModal}
          />
        );
      case "Landscaping":
        return (
          <LandscapingTable
            forms={forms}
            userRole={userRole}
            initiateEdit={initiateEdit}
            onDeleteClick={onDeleteClick}
            caption={caption}
            adminCountryLength={adminCountryLength}
            openEditModal={openEditModal}
          />
        );
      default:
        return <p>No table available for this form type</p>;
    }
  };

  return (
    <div>
      {renderTable()}
      {editModalOpen && modalRow && modalField && (
        <EditOwnerSupervisorModal
          isOpen={editModalOpen}
          toggle={closeEditModal}
          field={modalField}
          row={modalRow}
          formType={computedFormType}
          onUpdate={(updatedRow) => {
            handleUpdate(updatedRow);
          }}
        />
      )}
    </div>
  );
};

export default FormsTable;